import { useGetConversationSuspenseQuery } from "#features/conversations/api/useGetConversationSuspenseQuery.ts";
import { AssistantMessage } from "#features/conversations/components/conversation-view/AssistantMessage.tsx";
import { AssistantMessageBlocked } from "#features/conversations/components/conversation-view/AssistantMessageBlocked.tsx";
import { AssistantMessagePersonProfile } from "#features/conversations/components/conversation-view/AssistantMessagePersonProfile.tsx";
import { ConversationStartDate } from "#features/conversations/components/conversation-view/ConversationStartDate.tsx";
import { AssistantMessageConnection } from "#features/conversations/components/conversation-view/AssistantMessageConnection.tsx";
import { MessageToolApprovals } from "#features/conversations/components/conversation-view/MessageToolApprovals.tsx";
import { PendingPromptContext } from "#features/conversations/components/conversation-view/PendingPromptContext.ts";
import { ToolMessage } from "#features/conversations/components/conversation-view/ToolMessage.tsx";
import { UserMessage } from "#features/conversations/components/conversation-view/UserMessage.tsx";
import { InteractiveQuestionResponseMessage } from "#components/ui/interactive-question/InteractiveQuestionResponseMessage.tsx";
import { PreparedActionCard } from "#features/conversations/components/prepared-action/PreparedActionCard.tsx";
import { findPendingApproval } from "#features/conversations/lib/toolApprovalRecords.ts";
import { useConversationAutoScroll } from "#features/conversations/hooks/useConversationAutoScroll.ts";
import { useMessageCitations } from "#features/conversations/hooks/useMessageCitations.ts";
import { useCitedConversation } from "#features/conversations/hooks/useMessageContent.ts";
import { useIsCreateConversationMessageMutating } from "#features/conversations/hooks/useIsCreateConversationMessageMutating.ts";
import { useMemberDisplays } from "#features/conversations/hooks/useMemberDisplays.ts";
import { usePreparedActionSync } from "#features/conversations/hooks/usePreparedActionSync.ts";
import { useSkillDisplays } from "#features/conversations/hooks/useSkillDisplays.ts";
import { useComposerTrayStore } from "#features/conversations/stores/useComposerTrayStore.ts";
import type { ConversationMessageModel } from "#features/conversations/types/ConversationMessageModel.ts";
import { mentionedMemberIds } from "#features/conversations/utils/composerDocument.ts";
import { Div, Flex } from "@hopper-ui/components";
import { Fragment, useMemo, type ComponentProps, type RefObject } from "react";
import "./ConversationView.css";

interface Props {
    scrollableContainerRef: RefObject<HTMLDivElement | null>;
    conversationId: string;
}

// A turn that ended on the profile card replays it on its tool message, with no AssistantMessage
// above it to number its citations, and without the reply: only the card's prose is numbered.
const ToolMessagePersonProfile = ({ profile, message, cited }: Omit<ComponentProps<typeof AssistantMessagePersonProfile>, "citationNumbers">) => {
    const { numbersByIndex } = useMessageCitations(cited, message, { isProfileOnly: true });

    return <AssistantMessagePersonProfile profile={profile} message={message} cited={cited} citationNumbers={numbersByIndex} />;
};

// Each message's mentions, read once: a streamed reply replaces the message list but keeps every other message as it was.
const mentionedIdsByMessage = new WeakMap<ConversationMessageModel, string[]>();

const mentionedIdsOf = (message: ConversationMessageModel) => {
    let ids = mentionedIdsByMessage.get(message);

    if (!ids) {
        ids = mentionedMemberIds(message.content);
        mentionedIdsByMessage.set(message, ids);
    }

    return ids;
};

// A decision-only tool-approval post is persisted as an empty user turn; it shows no bubble.
const isBlankUserTurn = ({ role, content, files, isBlocked }: ConversationMessageModel) =>
    role === "user" && !content?.trim() && !files?.length && !isBlocked;

// Each approval or connection request resumes as a run of its own, saved as a new assistant message. When it follows a
// message that ended on that card, the two read as one answer. Returns the indexes of both halves.
const findResumedAnswers = (messages: ConversationMessageModel[]) => {
    const continuations = new Set<number>();
    const continued = new Set<number>();
    let previous: ConversationMessageModel | undefined;
    let previousIndex = -1;

    messages.forEach((message, index) => {
        if (message.role === "assistant" && previous?.role === "assistant" && (previous.toolApprovals?.length || previous.connection)) {
            continuations.add(index);
            continued.add(previousIndex);
        }
        if (!isBlankUserTurn(message) || message.toolApprovals?.length) {
            previous = message;
            previousIndex = index;
        }
    });

    return { continuations, continued };
};

export const ConversationView = ({ scrollableContainerRef, conversationId }: Props) => {
    const { data: conversation } = useGetConversationSuspenseQuery(conversationId);
    const cited = useCitedConversation(conversation);
    const isMutating = useIsCreateConversationMessageMutating(conversationId);
    const {
        conversationContainerRef,
        getLastMessageRef,
        pendingPromptRef
    } = useConversationAutoScroll(scrollableContainerRef, conversationId, conversation.messages.length, findPendingApproval(conversation) !== null);
    const unsettledDrafts = usePreparedActionSync(conversationId, conversation.messages);
    const isTrayOpen = useComposerTrayStore(state => state.isTrayOpen);
    const skillDisplays = useSkillDisplays();
    const mentionedIds = useMemo(
        () => conversation.messages.flatMap(message => (message.role === "user" && message.content ? mentionedIdsOf(message) : [])),
        [conversation.messages]
    );
    const memberDisplays = useMemberDisplays(mentionedIds);
    const { continuations, continued } = findResumedAnswers(conversation.messages);

    const renderMessage = (message: ConversationMessageModel, index: number) => {
        const { role } = message;

        switch (role) {
            case "tool":
                // A drafted action shows once its approval is answered; while it is pending the prompt holds it, in the same spot.
                if (message.preparedAction) {
                    return message.preparedAction.status === "pending"
                        ? <MessageToolApprovals key={message.id} conversationId={conversationId} message={message} unsettled={unsettledDrafts} />
                        : <PreparedActionCard key={message.id} state={message.preparedAction} isUnsettled={unsettledDrafts.has(message.id)} display={message.preparedAction.display} />;
                }

                // A turn that ended on the card, with no reply after it, replays the card on its tool message.
                return message.personProfile
                    ? <ToolMessagePersonProfile key={message.id} profile={message.personProfile} message={message} cited={cited} />
                    : <ToolMessage key={message.id} message={message} />;
            case "user": {
                // A turn that paused before any assistant text shows its approval on the message that started it.
                const toolApprovals = <MessageToolApprovals key={message.id} conversationId={conversationId} message={message} unsettled={unsettledDrafts} isTurnBlock />;

                // A decision-only tool-approval post is persisted as an empty user turn; show only its
                // approvals, not a blank bubble.
                // A request anchored on that turn, when the resumed run asks again, shows there too.
                const connection = message.connection && <AssistantMessageConnection conversationId={conversationId} connection={message.connection} isSettled />;
                if (isBlankUserTurn(message)) {
                    return (
                        <Fragment key={message.id}>
                            {toolApprovals}
                            {connection}
                        </Fragment>
                    );
                }

                return (
                    <Flex key={message.id} direction="column" gap="inline-lg">
                        <UserMessage message={message} skillDisplays={skillDisplays} memberDisplays={memberDisplays} />
                        {message.isBlocked && <AssistantMessageBlocked ref={getLastMessageRef(index)} />}
                        {toolApprovals}
                        {/* Core anchors a request that paused the turn before any assistant text on the message that started it. */}
                        {connection}
                    </Flex>
                );
            }
            case "assistant":
                return (
                    <AssistantMessage
                        key={message.id}
                        message={message}
                        cited={cited}
                        unsettledApprovals={unsettledDrafts}
                        isContinuation={continuations.has(index)}
                        isContinued={continued.has(index)}
                        ref={getLastMessageRef(index)}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <Flex
            ref={scrollableContainerRef}
            overflowY="auto"
            grow={1}
            className="wai-conversation-scroller"
            style={{ scrollbarGutter: "stable" }}
        >
            <Div
                UNSAFE_maxWidth="var(--wai-page-max-width)"
                marginX="auto"
                paddingX="inset-lg"
                paddingTop="inset-lg"
                width="100%"
                className="wai-conversation-view wai-composer-tray-backdrop"
                data-tray-open={isTrayOpen || undefined}
            >
                <Flex ref={conversationContainerRef} direction="column" gap="inline-lg" >
                    <ConversationStartDate conversation={conversation} />
                    <PendingPromptContext.Provider value={pendingPromptRef}>
                        {conversation.messages.map((message, index) => {
                            const { question } = message;
                            const node = renderMessage(message, index);

                            // The answers read as the user's own turn, right after the message the question anchors on, whatever its role.
                            return (
                                <Fragment key={message.id}>
                                    {node}
                                    {question?.status === "answered" && question.answers && <InteractiveQuestionResponseMessage answers={question.answers} />}
                                </Fragment>
                            );
                        })}
                    </PendingPromptContext.Provider>
                </Flex>
                {/* This anchor is used to stick the scroll position to the bottom of the conversation when content is appended to the AssistantMessage */}
                {/* This behavior only happens if the user starts scrolling while mutating and scrolls to the bottom of the conversation */}
                {/* Look into useConversationAutoScroll.ts for more information */}
                {isMutating && <Div id="wai-conversation-view-anchor" />}
            </Div>
        </Flex>
    );
};
